import { HttpClient } from '@angular/common/http';
import { EventEmitter, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { waitSeconds } from '@reflact/tsutil';
import { InactiveBotServiceService } from '@shared-angular/services/inactive-bot-service.service';
import { MsgProcessingService } from '@shared-angular/services/msg-processing.service';
import { PromptbarManagerService } from '@shared-angular/services/promptbar-manager.service';
import { RagLoggerService } from '@shared-angular/services/rag-logger/rag-logger.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { BotLocale, BotMessageFeedback, ChatbotConfig, HistoryItemResponse, MessageItem } from '@shared/index';
import dayjs, { Dayjs } from 'dayjs';
import { firstValueFrom } from 'rxjs';
import { MessageManagerService } from './message-manager.service';
import { TurnLatencyService } from './turn-latency/turn-latency.service';

export type AskSummaryResponse = {
  id: string,
  created: number,
  model: string,
  choices: {
    index: number,
    message: {
      role: "assistant" | "system" | "user" | "developer" | "botcard",
      content: string,
      refusal: null,
      annotations: unknown[]
    },
  }[],
  usage: {
    prompt_tokens: number,
    completion_tokens: number,
    total_tokens: number,
    completion_tokens_details: {
      reasoning_tokens: number,
      audio_tokens: number,
      accepted_prediction_tokens: number,
      rejected_prediction_tokens: number
    }
  }
}

export enum BOTEVENT {
  ServerSideThreadReady,
  WelcomeAndHistoryFinished,
  FeedbackCreateStart,
  FeedbackCreateEnd,
  FeedbackDeleteStart,
  FeedbackDeleteEnd,
  HistoryMessagesLoadStart,
  HistoryMessagesLoadEnd,
  HistoryMessagesDeleteStart,
  HistoryMessagesDeleteEnd,
  CancelThinkingStart,
  CancelThinkingEnd,
  runStarted,
  runDelta,
  runFinished,
  avatarConnected,
}

/**
 * Interne API des Bots (`RAGAI.BOTMANAGER`): Chat, Statistiken, Feedback, Verlauf und Konfiguration.
 * @remarks
 * Ist eine interne API, die sich zwischen Versionen ändern kann - wo möglich sollten stattdessen die
 * dokumentierten, stabileren RAGAI-Module verwendet werden (`RAGAI.MSG`, `RAGAI.HELPER`, `RAGAI.DIALOGSTORE` etc.),
 * die intern teils selbst auf `BotService` aufbauen. Viele Methoden hier sind dünne HTTP-Wrapper um
 * `/api/toolbox/...`-Endpunkte und lösen dabei passende `botEvents`-Einträge (`BOTEVENT`-Enum) aus, z. B.
 * `HistoryMessagesLoadStart`/`...LoadEnd` rund um {@link getHistoryMessages}.
 */
@Injectable({
  providedIn: 'root'
})
export class BotService {
  /** Feuert, nachdem `resetBot()` vollständig abgeschlossen ist. */
  public resetBotDoneEmitter: EventEmitter<void> = new EventEmitter<void>();
  /** Feuert bei jedem Lebenszyklus-Schritt der Bot-API (Start/Ende von HTTP-Aufrufen), Typ siehe `BOTEVENT`-Enum. */
  public botEvents: EventEmitter<{ type: BOTEVENT, data: unknown }> = new EventEmitter<{ type: BOTEVENT, data: unknown }>();
  public botConfig?: ChatbotConfig;
  private turnLatency = inject(TurnLatencyService);

  constructor(
    public msgService: MessageManagerService,
    private http: HttpClient,
    public inActiveService: InactiveBotServiceService,
    public toastr: ToastrService,
    public logger: RagLoggerService,
    public promptBar: PromptbarManagerService,
    private beforeSendInterceptor: MsgProcessingService,
    private router: Router
  ) { }

  /**
   * Scrollt den Chatverlauf ans Ende und fokussiert das Eingabefeld.
   * @param scrollBehavior Scroll-Verhalten (z. B. `'smooth'` oder `'auto'`).
   */
  public async scrollDown(scrollBehavior: ScrollBehavior = 'smooth') {
    const scrollSelector = '#bot-content', inputId = 'promptInput';
    await waitSeconds(0.1);
    const scrollElement = document.querySelector(scrollSelector);
    if (scrollElement) {
      scrollElement.scrollTo({ top: scrollElement.scrollHeight, behavior: scrollBehavior });
    }

    await this.focusInput(inputId);
  }

  private async focusInput(inputId: string) {
    await waitSeconds(0.1);
    document.getElementById(inputId)?.focus({ preventScroll: true });
  }

  /**
   * Erfasst eine Statistik-Kennzahl für den Bot.
   * @param botId ID des Bots.
   * @param stat Name der zu erfassenden Statistik.
   * @returns Status der Erfassung.
   */
  public async addStat(botId: string, stat: string): Promise<{ status: string }> {
    return await firstValueFrom(this.http.get<{ status: string }>('/api/toolbox/membot/user/addStat/' + botId + '/' + stat));
  }

  /**
   * Sendet eine Chat-Nachricht an den Bot und löst einen Antwort-Run aus.
   * @param botId ID des Bots.
   * @param message Der Nachrichtentext.
   * @param history Bisheriger Nachrichtenverlauf.
   * @param bypass `true`, wenn die Nachricht ohne Verarbeitung direkt in den Verlauf soll.
   * @returns Die Bot-Antwort und die Thread-ID, oder `undefined` bei Abbruch durch einen BEFORE_SEND-Hook.
   */
  public async chat(botId: string, message: string, history: MessageItem[], bypass: boolean): Promise<{ msg: string, threadId: string } | undefined> {
    this.inActiveService.botWasActive();
    this.msgService.onUserToBot.emit(message);
    const processedMsg = await this.beforeSendInterceptor.run(message);
    if (processedMsg === null) {
      return undefined;
    }
    message = processedMsg;
    this.botEvents.emit({ type: BOTEVENT.runStarted, data: { botId, message } });
    const msg: (MessageItem & { bypass?: boolean }) = {
      content: [{
        text: message,
        type: 'text'
      }],
      role: "user"
    };
    if (bypass) {
      msg.bypass = bypass;
    }
    const turnId = this.turnLatency.consumeTurnIdForChat();
    return await firstValueFrom(this.http.post<{ msg: string, threadId: string }>("/api/user/chat/" + botId + "/chat", { message: [msg], history, turnId }));
  }

  /**
   * Sendet die Antwort auf eine MCP-Tool-Freigabeanfrage an den Bot.
   * @param botId ID des Bots.
   * @param message Die Freigabe-Antwortnachricht.
   * @param history Bisheriger Nachrichtenverlauf.
   * @returns Die Bot-Antwort und die Thread-ID.
   */
  public async mcpApprovalResponse(botId: string, message: MessageItem, history: MessageItem[]) {
    console.log(history);
    return await firstValueFrom(this.http.post<{ msg: string, threadId: string }>("/api/user/chat/" + botId + "/chat", { message: [message], history }));
  }

  /**
   * Löscht einzelne Nachrichten aus dem Verlauf.
   * @param botId ID des Bots.
   * @param messageIds IDs der zu löschenden Nachrichten.
   * @returns Ergebnis der Löschoperation.
   */
  public async deleteHistoryMessages(botId: string, messageIds: string[]) {
    this.botEvents.emit({ type: BOTEVENT.FeedbackDeleteStart, data: { botId, messageIds } });
    const result = await firstValueFrom(this.http.delete('/api/toolbox/membot/user/memory/messages/' + botId, { body: { messageIds } }));
    this.botEvents.emit({ type: BOTEVENT.FeedbackDeleteEnd, data: { botId, messageIds, result } });
    return result;
  }

  /**
   * Lädt den gespeicherten Nachrichtenverlauf des Bots.
   * @param botId ID des Bots.
   * @returns Der Nachrichtenverlauf.
   */
  public async getHistoryMessages(botId: string): Promise<{ messageHistory: HistoryItemResponse[] }> {
    this.botEvents.emit({ type: BOTEVENT.HistoryMessagesLoadStart, data: { botId } });
    const messageHistory = await firstValueFrom(this.http.get<{ messageHistory: HistoryItemResponse[] }>('/api/toolbox/membot/user/memory/messages/' + botId));

    this.botEvents.emit({ type: BOTEVENT.HistoryMessagesLoadEnd, data: { botId, messageHistory } });
    return messageHistory;
  }

  /**
   * Benachrichtigt den Supervisor über eine Nachricht, die Hilfe benötigt.
   * @param botId ID des Bots.
   * @param historyItemId ID der betroffenen Verlaufsnachricht.
   * @param messageName Name/Typ der Benachrichtigung.
   * @returns Nichts.
   */
  public async supervisorNotification(botId: string, historyItemId: string, messageName: string): Promise<void> {
    await firstValueFrom(this.http.post(`/api/toolbox/membot/user/supervisorhelprequest/${botId}/${historyItemId}`, { messageName }));
  }

  /**
   * Ruft den konfigurierten Webhook eines Skills auf.
   * @param botId ID des Bots.
   * @param skillId ID des Skills, dessen Webhook aufgerufen wird.
   * @param data Zusätzliche Daten, die an den Webhook gesendet werden.
   * @returns Die Antwort des Webhooks als Text.
   */
  public async callWebhook(botId: string, skillId: string, data?: unknown): Promise<unknown> {
    return await firstValueFrom(this.http.post(`/api/toolbox/membot/user/callwebhook/${botId}/${skillId}`, { data }, { responseType: "text" }));
  }

  /**
   * Löscht Verlaufseinträge in einem Zeitraum.
   * @param botId ID des Bots.
   * @param body Zeitraum-Filter.
   * @param body.fromDate Beginn des Zeitraums.
   * @param body.toDate Ende des Zeitraums.
   * @returns Status und Anzahl gelöschter Einträge.
   */
  public async deleteHistoryItems(botId: string, body: { fromDate?: Dayjs, toDate?: Dayjs, }): Promise<{ status: number, deletedCount: number }> {
    this.botEvents.emit({ type: BOTEVENT.HistoryMessagesDeleteStart, data: { botId, body } });
    const deleteResult = await firstValueFrom(this.http.post<{ status: number, deletedCount: number }>("/api/toolbox/bot/admin/deleteHistoryItems/" + botId, body));
    this.botEvents.emit({ type: BOTEVENT.HistoryMessagesDeleteEnd, data: { botId, body, deleteResult } });
    return deleteResult;
  }

  /**
   * Fasst eine Liste von Verlaufsnachrichten per KI zusammen.
   * @param botId ID des Bots.
   * @param body Zusammenzufassende Nachrichten und optionaler Zeitraum.
   * @param body.prompts Die zusammenzufassenden Nachrichten.
   * @param body.fromDate Beginn des Zeitraums.
   * @param body.toDate Ende des Zeitraums.
   * @returns Die KI-generierte Zusammenfassung.
   */
  public async askSummary(botId: string, body: { prompts: HistoryItemResponse[], fromDate?: Dayjs, toDate?: Dayjs, }): Promise<AskSummaryResponse> {
    return await firstValueFrom(this.http.post<AskSummaryResponse>("/api/toolbox/membot/user/askSummary/" + botId, body));
  }

  /**
   * Sendet die Antwort eines Admins auf eine Supervisor-Hilfeanfrage an den Bot.
   * @param botId ID des Bots.
   * @param adminMsgId ID der Supervisor-Nachricht, auf die geantwortet wird.
   * @param message Die Antwort des Admins.
   * @returns Die Antwort des Bots.
   */
  public async answerSupervisor(botId: string, adminMsgId: string, message: string): Promise<string> {
    return await firstValueFrom(this.http.post<string>("/api/toolbox/membot/user/supervisorAnswer/" + botId + "/" + adminMsgId, { message }));
  }

  /**
   * Lädt den Standard-Datenschutz-/Compliance-Text für eine Sprache.
   * @param locale Die gewünschte Sprache.
   * @returns Der Compliance-Text.
   */
  public async getDefaultComplianceText(locale: BotLocale): Promise<string> {
    return await firstValueFrom(this.http.get('/api/toolbox/bot/admin/defaultComplianceText/' + locale, { responseType: 'text' }));
  }

  /**
   * Lädt Metadaten zum gespeicherten Verlauf des Bots (z. B. Anzahl Einträge).
   * @param botId ID des Bots.
   * @returns Status und Anzahl der Verlaufseinträge.
   */
  public async getHistoryInfo(botId: string): Promise<{ status: string, items: { _id: string, count: number }[] }> {
    return await firstValueFrom(this.http.get<{ status: string, items: { _id: string, count: number }[] }>("/api/toolbox/bot/admin/historyInfo/" + botId));
  }

  /**
   * Zählt Verlaufseinträge in einem Zeitraum.
   * @param botId ID des Bots.
   * @param body Zeitraum-Filter.
   * @param body.fromDate Beginn des Zeitraums.
   * @param body.toDate Ende des Zeitraums.
   * @returns Status und Anzahl der gefundenen Einträge.
   */
  public async countHistoryItemsByFilter(botId: string, body: { fromDate?: Dayjs, toDate?: Dayjs }): Promise<{ status: string, count: number }> {
    return await firstValueFrom(this.http.post<{ status: string, count: number }>("/api/toolbox/bot/admin/countHistoryItemsByFilter/" + botId, body));
  }

  /**
   * Sendet Feedback zu einer Bot-Nachricht.
   * @param feedbackId ID eines bestehenden Feedbacks (zum Aktualisieren), oder `undefined` für neues Feedback.
   * @param feedback Der Feedback-Inhalt.
   * @returns Status und das gespeicherte Feedback.
   */
  public async sendFeedback(feedbackId: string | undefined, feedback: Omit<BotMessageFeedback, '_id' | 'created'>) {
    this.botEvents.emit({ type: BOTEVENT.FeedbackCreateStart, data: { feedbackId, feedback } });
    const result = await firstValueFrom(this.http.post<{ status: string, feedback?: BotMessageFeedback }>('/api/toolbox/feedback/add', { feedbackId, feedback }));
    this.botEvents.emit({ type: BOTEVENT.FeedbackCreateEnd, data: { feedbackId, feedback, result } });
    return result;
  }

  /**
   * Löscht ein Feedback.
   * @param feedbackId ID des zu löschenden Feedbacks.
   * @returns Status der Löschung.
   */
  public async deleteFeedback(feedbackId: string) {
    this.botEvents.emit({ type: BOTEVENT.FeedbackDeleteStart, data: { feedbackId } });
    const result = await firstValueFrom(this.http.delete<{ status: string }>('/api/toolbox/feedback/' + feedbackId));
    this.botEvents.emit({ type: BOTEVENT.FeedbackDeleteEnd, data: { feedbackId, result } });
    return result;
  }

  /**
   * Lädt alle Feedbacks eines Bots.
   * @param botId ID des Bots.
   * @returns Status und die Feedbacks.
   */
  public async getFeedback(botId: string) {
    return await firstValueFrom(this.http.get<{ status: string, feedbacks: BotMessageFeedback[] }>('/api/toolbox/feedback/' + botId));
  }

  /**
   * Lädt den Rohinhalt einer herunterladbaren Datei (Skill-Material).
   * @param botId ID des Bots.
   * @param fileId ID der Datei.
   * @returns Der Dateiinhalt als ArrayBuffer.
   */
  public async getDownloadableMaterial(botId: string, fileId: string) {
    return await firstValueFrom(this.http.get('/api/toolbox/bot/user/downloadfile/' + botId + '/' + fileId, { responseType: 'arraybuffer' }));
  }

  /**
   * Lädt den Markdown-Inhalt eines Skill-Dokuments.
   * @param botId ID des Bots.
   * @param skillId ID des Skills.
   * @returns Metadaten und Markdown-Inhalt des Dokuments.
   */
  public async getMDFile(botId: string, skillId: string) {
    return await firstValueFrom(this.http.get<{ id: string, name: string, type: string, content: string }>('/api/toolbox/bot/user/downloadmdfile/' + botId + '/' + skillId));
  }

  /**
   * Lädt Metadaten (Name, Typ, ID) einer Skill-Datei.
   * @param botId ID des Bots.
   * @param skillId ID des Skills.
   * @returns Die Datei-Metadaten.
   */
  public async getFileInformation(botId: string, skillId: string) {
    return await firstValueFrom(this.http.get<{ name: string, type: string, id: string }>('/api/toolbox/bot/user/fileinformation/' + botId + '/' + skillId));
  }

  /**
   * Lädt die eigenen Feedbacks des aktuellen Nutzers.
   * @param botId ID des Bots.
   * @returns Status und die eigenen Feedbacks.
   */
  public async getMyFeedback(botId: string) {
    return await firstValueFrom(this.http.get<{ status: string, feedbacks: BotMessageFeedback[] }>('/api/toolbox/feedback/' + botId + "/myfeedback"));
  }

  /**
   * Bricht einen laufenden Denk-/Antwortvorgang des Bots ab.
   * @param botId ID des Bots.
   * @param threadId ID des abzubrechenden Threads.
   * @returns Die Antwort des Servers.
   */
  public async sendBotThinkingCancel(botId: string, threadId: string) {
    this.botEvents.emit({ type: BOTEVENT.CancelThinkingStart, data: { botId, threadId } });
    const result = await firstValueFrom(this.http.post<{ message: string }>('/api/toolbox/membot/user/cancelthinking/' + botId, { threadId }));
    this.botEvents.emit({ type: BOTEVENT.CancelThinkingEnd, data: { botId, threadId } });
    return result;
  }


  /**
   * Setzt den Bot für den aktuellen Nutzer zurück (neue Sitzung, neue Begrüßung).
   * @param botId ID des Bots.
   * @returns Die Antwort des Servers.
   */
  public async resetBot(botId: string) {
    //vorher Emitten sonst fehlt begrüßung
    this.resetBotDoneEmitter.emit();
    const result = await firstValueFrom(this.http.get<{ message: string }>('/api/toolbox/membot/user/resetbot/' + botId));
    return result;
  }


  /**
   * Prüft, ob der aktuelle Nutzer Admin-Zugriff auf den Bot hat.
   * @param id ID des Bots.
   * @returns `true`, wenn Admin-Zugriff besteht.
   */
  public async hasBotAccessByAdmin(id: string): Promise<boolean> {
    try {
      const result = await firstValueFrom(this.http.get<{ hasAccess: boolean }>("/api/toolbox/bot/user/canaccessasadmin/" + id));
      return result.hasAccess;
    } catch (e) {
      console.warn(e);
      return false;
    }

  }
  /**
   * Lädt die Bot-Konfiguration aus Nutzersicht und speichert sie zwischen.
   * @param id ID des Bots.
   * @returns Die Bot-Konfiguration, oder `null` bei Fehler.
   */
  public async getBotConfigAsUser(id: string) {
    try {
      const botConfig = await firstValueFrom(this.http.get<ChatbotConfig>("/api/toolbox/bot/user/botconfig/" + id));
      this.botConfig = botConfig;
      return botConfig;
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  /**
   * Lässt per KI einen Bot-Namen anhand einer Beschreibung vorschlagen.
   * @param botId ID des Bots.
   * @param description Beschreibung des Bot-Zwecks.
   * @returns Der vorgeschlagene Name (max. 30 Zeichen).
   */
  public async suggestName(botId: string, description: string): Promise<string> {
    let answer = (await firstValueFrom(this.http.post<{ name: string }>('/api/toolbox/bot/admin/suggestName', { botId, description }))).name;
    if (answer.length > 30) {
      answer = answer.substring(0, 30);
    }
    return answer;
  }

  /**
   * Lässt per KI einen Skill-Funktionsnamen anhand einer Beschreibung vorschlagen.
   * @param botId ID des Bots.
   * @param description Beschreibung der Skill-Funktion.
   * @param functionname Bisheriger Funktionsname als Kontext für den Vorschlag.
   * @returns Der vorgeschlagene Funktionsname (max. 15 Zeichen).
   */
  public async suggestSkill(botId: string, description: string, functionname: string): Promise<string> {
    let answer = (await firstValueFrom(this.http.post<{ skill: string }>('/api/toolbox/bot/admin/suggestSkill', { botId, description, functionname }))).skill;
    if (answer.length > 15) {
      answer = answer.substring(0, 15);
    }
    return answer;
  }

  /**
   * Ermittelt die Bot-Farbe aus der Konfiguration, optional als transparente Hintergrundvariante.
   * @param botConfig Die Bot-Konfiguration.
   * @param background `true`, um eine abgeschwächte Hintergrundfarbe zu liefern.
   * @returns Der Farbwert als CSS-Farbstring.
   */
  public getColorFromConfig(botConfig: ChatbotConfig, background: boolean = false): string {
    if (botConfig.color === 'transparent') {
      return 'transparent';
    }
    if (background) {
      return botConfig.color ? botConfig.color + '33' : '#cccccc50';
    } else {
      return botConfig.color ? botConfig.color : '#cccccc';
    }
  }

  // UI HELPER
  /**
   * Ermittelt, welche Deeplink-Ansicht (Task oder Verlauf) für den Bot standardmäßig geöffnet wird.
   * @param bot Die Bot-Konfiguration.
   * @returns `"task"` oder `"information"`.
   */
  public getBotDeeplink(bot: ChatbotConfig): "task" | "information" {
    if (!bot.showChangeHistory) {
      return "task";
    } else {
      return "information";
    }
  }

  /**
   * Lädt die Bot-Konfiguration als ZIP herunter.
   * @param botConfig Die zu exportierende Bot-Konfiguration.
   */
  public async exportBotConfigZIP(botConfig: ChatbotConfig) {
    const zip = await firstValueFrom(this.http.get("/api/toolbox/bot/admin/exportaszip/" + botConfig._id, { responseType: 'arraybuffer' }));
    const fileName = botConfig.name + "_" + dayjs().format('YYYY-MM-DD') + ".zip";
    this.saveAs(new Blob([zip], { type: 'application/zip' }), fileName);
  }
  /* public async importBotConfigZIP(file: File, projectId: string) {
    console.log("upload");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("projectId", projectId);
    this.toastr.success("GENERAL", $localize`:@@import-bot-file-selected:Bot wird importiert und anschließend geöffnet`);
    const res = await firstValueFrom(this.http.post<{ status: "ok" | "error", botId?: string }>("/api/toolbox/bot/admin/importaszip", formData));
    if (res.status == "ok") {
      await this.router.navigate(['/bot-admin', res.botId, 'task']);
    } else {
      this.toastr.error("ERROR", $localize`:@@import-bot-file-error:Bot konnte nicht importiert werden`);
    }
    return res;
  } */

  /**
   * Löst im Browser den Download einer Datei aus.
   * @param blob Der Dateiinhalt.
   * @param filename Der vorgeschlagene Dateiname.
   */
  public saveAs(blob: Blob, filename: string) {
    const fileURL = URL.createObjectURL(blob);
    const element = document.createElement('a');
    element.setAttribute('href', fileURL);
    element.setAttribute('download', filename);
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

}
